import { Moment } from "moment";
import { useMemo } from "react";

import DatePicker, { CustomDatePickerProps, CustomRangePickerProps } from "../DatePicker";

/**
 * Props for the DatePickerAvailableDaysWrapper component
 * Extends CustomDatePickerProps with additional date range configuration
 */
export type DatePickerWrapperProps = CustomDatePickerProps & {
  /** Type of available date range calculation */
  availableDatesGetterType?:
    | "ActualDays"
    | "TwelveMonthsBeforeEndOfEvent"
    | "ActualDays5DaysShift"
    | "TwelveMonthsAfterEndOfEvent"
    | "AfterEndOfEvent"
    | "TwelveMonthsBeforeStartOfEvent";
  /** Start date of the event for date range calculations */
  eventStartDate?: Moment;
  /** End date of the event for date range calculations */
  eventEndDate?: Moment;
  /** Default value to show in the picker when no value is selected */
  defaultPickerValue?: Moment;
};

/**
 * Determines if a given date should be disabled in the date picker
 * @param value - The date to check
 * @param beforeDate - Dates before this date will be disabled
 * @param afterDate - Dates after this date will be disabled
 * @returns true if the date should be disabled, false otherwise
 */
const disabledDatesGetter = (value: Moment, beforeDate?: Moment, afterDate?: Moment) => {
  return (
    (beforeDate ? value.isBefore(beforeDate, "day") : false) || (afterDate ? value.isAfter(afterDate, "day") : false)
  );
};

/**
 * DatePickerAvailableDaysWrapper Component
 *
 * A wrapper around DatePicker that automatically calculates and restricts available dates
 * based on event dates and specified date range types.
 *
 * Supports six modes:
 * - "ActualDays": Allows selection between event start and end dates +-1 day
 * - "TwelveMonthsBeforeEndOfEvent": Allows selection from 12 months before event end date up to event end date
 * - "ActualDays5DaysShift": Allows selection between event start and end dates with a 5-day shift
 * - "TwelveMonthsAfterEndOfEvent": Allows selection from event end date up to 12 months after event end date
 * - "AfterEndOfEvent": Allows selection from event end date onwards
 * - "TwelveMonthsBeforeStartOfEvent": Allows selection from 12 months before event start date up to the day before event start
 */
const DatePickerAvailableDaysWrapper = ({
  availableDatesGetterType,
  eventStartDate,
  eventEndDate,
  defaultPickerValue,
  value,
  ...props
}: DatePickerWrapperProps) => {
  // Currently disabled - would prevent selection if current date is after event end
  const isPickerDisabled = false; //moment().isAfter(eventEndDate, "day");

  // Calculate date boundaries based on the specified type using useMemo for performance
  const { beforeDate, afterDate } = useMemo(() => {
    if (availableDatesGetterType === "ActualDays") {
      // Allow selection between event start and end dates
      // beforeDate: 1 day before event start (so event start is selectable)
      // afterDate: 1 day after event end (so event end is selectable)
      return {
        beforeDate: eventStartDate?.clone().subtract(1, "days"),
        afterDate: eventEndDate?.clone().add(1, "days"),
      };
    } else if (availableDatesGetterType === "TwelveMonthsBeforeEndOfEvent") {
      // Allow selection from 12 months before the event end date up to the event end date
      // beforeDate: event end date (so dates before event end are disabled)
      // afterDate: 12 months and 1 day after event end
      return {
        afterDate: eventEndDate?.clone(),
        beforeDate: eventEndDate?.clone().subtract({ months: 12 }).add(1, "days"),
      };
    } else if (availableDatesGetterType === "ActualDays5DaysShift") {
      // Allow selection between event start and end dates with a 5-day shift
      // beforeDate: 5 days before event start (so event start is selectable)
      // afterDate: 5 days after event end (so event end is selectable)
      return {
        beforeDate: eventStartDate?.clone().subtract(5, "days"),
        afterDate: eventEndDate?.clone().add(5, "days"),
      };
    } else if (availableDatesGetterType === "TwelveMonthsAfterEndOfEvent") {
      // Allow selection from event end date up to 12 months after event end date
      return {
        beforeDate: eventEndDate?.clone(),
        afterDate: eventEndDate?.clone().add({ months: 12 }).add(1, "days"),
      };
    } else if (availableDatesGetterType === "AfterEndOfEvent") {
      // Allow selection from event end date onwards
      return {
        beforeDate: eventEndDate?.clone(),
        afterDate: undefined,
      };
    } else if (availableDatesGetterType === "TwelveMonthsBeforeStartOfEvent") {
      // Allow selection up to the day before the event start date
      return {
        beforeDate: eventStartDate?.clone().subtract({ months: 12 }),
        afterDate: eventStartDate?.clone().subtract(1, "days"),
      };
    }

    // Default case - no date restrictions
    return {
      beforeDate: undefined,
      afterDate: undefined,
    };
  }, [availableDatesGetterType, eventStartDate, eventEndDate]);

  return (
    <DatePicker
      defaultPickerValue={value ?? defaultPickerValue}
      disabledDate={(currentDate: Moment) => disabledDatesGetter(currentDate, beforeDate, afterDate)}
      disabled={isPickerDisabled}
      value={value}
      {...props}
    />
  );
};

const { RangePicker } = DatePicker;

export type RangePickerWrapperProps = CustomRangePickerProps & {
  /** Type of available date range calculation */
  availableDatesGetterType?:
    | "ActualDays"
    | "TwelveMonthsBeforeEndOfEvent"
    | "ActualDays5DaysShift"
    | "TwelveMonthsAfterEndOfEvent"
    | "AfterEndOfEvent"
    | "TwelveMonthsBeforeStartOfEvent";
  /** Start date of the event for date range calculations */
  eventStartDate?: Moment;
  /** End date of the event for date range calculations */
  eventEndDate?: Moment;
  /** Default value to show in the picker when no value is selected (tuple for range) */
  defaultPickerValue?: [Moment, Moment];
};

const RangePickerAvailableDaysWrapper = ({
  availableDatesGetterType,
  eventStartDate,
  eventEndDate,
  defaultPickerValue,
  value,
  ...props
}: RangePickerWrapperProps) => {
  // Currently disabled - would prevent selection if current date is after event end
  const isPickerDisabled = false; //moment().isAfter(eventEndDate, "day");

  // Calculate date boundaries based on the specified type using useMemo for performance
  const { beforeDate, afterDate } = useMemo(() => {
    if (availableDatesGetterType === "ActualDays") {
      // Allow selection between event start and end dates
      // beforeDate: 1 day before event start (so event start is selectable)
      // afterDate: 1 day after event end (so event end is selectable)
      return {
        beforeDate: eventStartDate?.clone().subtract(1, "days"),
        afterDate: eventEndDate?.clone().add(1, "days"),
      };
    } else if (availableDatesGetterType === "TwelveMonthsBeforeEndOfEvent") {
      // Allow selection from 12 months before the event end date up to the event end date
      // beforeDate: event end date (so dates before event end are disabled)
      // afterDate: 12 months and 1 day after event end
      return {
        afterDate: eventEndDate?.clone(),
        beforeDate: eventEndDate?.clone().subtract({ months: 12 }).add(1, "days"),
      };
    } else if (availableDatesGetterType === "ActualDays5DaysShift") {
      // Allow selection between event start and end dates with a 5-day shift
      // beforeDate: 5 days before event start (so event start is selectable)
      // afterDate: 5 days after event end (so event end is selectable)
      return {
        beforeDate: eventStartDate?.clone().subtract(5, "days"),
        afterDate: eventEndDate?.clone().add(5, "days"),
      };
    } else if (availableDatesGetterType === "TwelveMonthsAfterEndOfEvent") {
      // Allow selection from event end date up to 12 months after event end date
      return {
        beforeDate: eventEndDate?.clone(),
        afterDate: eventEndDate?.clone().add({ months: 12 }).add(1, "days"),
      };
    } else if (availableDatesGetterType === "AfterEndOfEvent") {
      // Allow selection from event end date onwards
      return {
        beforeDate: eventEndDate?.clone(),
        afterDate: undefined,
      };
    } else if (availableDatesGetterType === "TwelveMonthsBeforeStartOfEvent") {
      // Allow selection up to the day before the event start date
      return {
        beforeDate: eventStartDate?.clone().subtract({ months: 12 }),
        afterDate: eventStartDate?.clone().subtract(1, "days"),
      };
    }

    // Default case - no date restrictions
    return {
      beforeDate: undefined,
      afterDate: undefined,
    };
  }, [availableDatesGetterType, eventStartDate, eventEndDate]);

  // Ensure we pass a [Moment, Moment] | undefined to RangePicker.defaultPickerValue
  const safeDefaultPickerValue: [Moment, Moment] | undefined = useMemo(() => {
    // If `value` is a tuple with two non-null Moments, use that as the default picker visible months.
    // Note: `defaultPickerValue` controls the displayed months in the calendar, not the selected value(s).
    // We prefer the explicit `value` when present so the calendar shows the same months as the current selection.
    if (Array.isArray(value) && value[0] && value[1]) {
      return [value[0] as Moment, value[1] as Moment];
    }
    // Otherwise use the explicit defaultPickerValue (already typed as [Moment, Moment] | undefined)
    return defaultPickerValue;
  }, [value, defaultPickerValue]);

  return (
    <RangePicker
      disabled={isPickerDisabled}
      defaultPickerValue={safeDefaultPickerValue}
      disabledDate={(currentDate: Moment) => disabledDatesGetter(currentDate, beforeDate, afterDate)}
      value={value}
      {...props}
    />
  );
};

DatePickerAvailableDaysWrapper.RangePickerAvailableDaysWrapper = RangePickerAvailableDaysWrapper;

export default DatePickerAvailableDaysWrapper;
